
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Search, Menu, ChevronDown, ShoppingCart, User, LogOut, LayoutDashboard, UserCircle, Upload, Settings, Activity, Calendar } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useCartContext } from '@/contexts/CartContext';
import { useFavoritesContext } from '@/contexts/FavoritesContext';
import { useUser } from '@/contexts/UserContext';
import { SessionManager } from '@/utils/sessionManager';
import { MenuService } from "@/services/menuService";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { MockPhotoService } from "@/services/mockPhotoService";
import { NotificationBell } from "@/components/NotificationBell";

const iconMap: Record<string, any> = {
  LayoutDashboard,
  Upload,
  Calendar,
  Activity,
  Settings,
  UserCircle,
  ShoppingCart,
  LogOut
};

export default function Navbar() {
  const [searchQuery, setSearchQuery] = useState("");
  const { totalItems, addToCart } = useCartContext();
  const { totalFavorites } = useFavoritesContext();
  const { user, isLoggedIn, logout, isLoading } = useUser();

  const { data: menuItems } = useQuery({
    queryKey: ['menus', user?.role],
    queryFn: () => MenuService.getMenus(user?.role || 'athlete'),
    enabled: !!user?.role,
    staleTime: 5 * 60 * 1000,
  });

  const [showResults, setShowResults] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [, setLocation] = useLocation();

  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length > 1) {
      try {
        const results = await MockPhotoService.search(query, 5);
        setSearchResults(results.photos);
        setShowResults(true);
      } catch (error) {
        console.error("Erro na busca:", error);
      }
    } else {
      setShowResults(false);
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setShowResults(false);
      // Redirecionar para busca avançada com parametro genérico 'q'
      const params = new URLSearchParams();
      params.append('q', searchQuery);
      setLocation(`/search?${params.toString()}`);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-green-dark text-white shadow-lg">
      <div className="relative z-10">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-14 min-h-[3.5rem] py-2">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              <img 
                src="/Logo_2.png" 
                alt="FotUP Logo" 
                className="h-8 sm:h-9 w-auto object-contain"
              />
              <div className="hidden sm:flex flex-col justify-center">
                <span className="text-base sm:text-lg font-bold text-white leading-tight">FotUP</span>
                <span className="text-[10px] sm:text-xs text-white/70 leading-tight">Fotografia Esportiva</span>
              </div>
            </Link>

            {/* Search Bar - Hidden on mobile */}
            <div className="hidden md:flex flex-1 max-w-md mx-8 relative">
              <div className="relative w-full">
                <Input
                  type="text"
                  placeholder="Buscar por evento ou número do peito..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyPress={handleKeyPress}
                  onBlur={() => setTimeout(() => setShowResults(false), 200)}
                  onFocus={() => searchQuery.length > 1 && setShowResults(true)}
                  className="w-full px-4 py-2 text-gray-800 bg-white square-input focus:outline-none focus:ring-2 focus:ring-white border-0"
                  data-testid="input-search"
                />
                <Button
                  onClick={handleSearch}
                  className="absolute right-0 top-0 h-full px-4 bg-primary hover:bg-secondary transition-colors border-0 square-input"
                  data-testid="button-search"
                >
                  <Search className="h-4 w-4 text-white" />
                </Button>
              </div>

              {/* Dropdown de Resultados */}
              {showResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-200 overflow-hidden z-50">
                  <div className="py-2">
                    {searchResults.map((photo) => (
                      <div 
                        key={photo.id}
                        className="px-4 py-2 hover:bg-gray-50 flex items-center gap-3 group"
                      >
                        <div 
                          className="flex-1 flex items-center gap-3 cursor-pointer min-w-0"
                          onClick={() => {
                            setLocation(`/photos/${photo.id}`);
                            setShowResults(false);
                          }}
                        >
                          <img src={photo.storageUrl} alt="" className="w-10 h-10 object-cover rounded" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{photo.event.title}</p>
                            <p className="text-xs text-gray-500 truncate">{photo.filename}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-primary">R$ {photo.price.toFixed(2)}</span>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-gray-400 hover:text-primary hover:bg-primary/10 opacity-0 group-hover:opacity-100 transition-all"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await addToCart(photo.id, 1);
                              } catch (error) {
                                console.error("Erro ao adicionar ao carrinho", error);
                              }
                            }}
                          >
                            <ShoppingCart className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-100 bg-gray-50 p-2 text-center">
                    <button
                      onClick={handleSearch}
                      className="text-sm text-primary hover:text-teal-700 font-medium w-full"
                    >
                      Ver mais resultados
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation Menu */}
            <nav className="hidden md:flex items-center space-x-4 lg:space-x-6 flex-shrink-0">
              <Link to="/" className="text-white hover:text-white/80 transition-colors font-medium whitespace-nowrap" data-testid="link-home">
                Home
              </Link>

              {/* Events Link */}
              <Link to="/events" className="text-white hover:text-white/80 transition-colors font-medium whitespace-nowrap" data-testid="link-events">
                Eventos
              </Link>






              {isLoading ? (
                <div className="w-8 h-8 bg-white/20 rounded-full animate-pulse" />
              ) : isLoggedIn && user ? (
                <>
                  {/* Favorites Icon */}
                  {/* <Link to="/favorites" className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10 relative"
                    data-testid="button-favorites"
                  >
                    <Heart className="h-5 w-5" />
                    {totalFavorites > 0 && (
                      <Badge 
                        className="absolute -top-2 -right-2 bg-red-500 text-white text-xs min-w-[1.25rem] h-5 flex items-center justify-center rounded-full px-1"
                      >
                        {totalFavorites}
                      </Badge>
                    )}
                  </Button>
                </Link> */}

                  {/* Notification Bell */}
                  <div className="relative flex-shrink-0">
                    <NotificationBell className="text-white hover:bg-white/10" />
                  </div>

                  {/* Cart Icon */}
                  <Link to="/cart" className="relative flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white hover:bg-white/10 relative h-9 w-9"
                      data-testid="button-cart"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      {totalItems > 0 && (
                        <Badge
                          className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] min-w-[1.125rem] h-4 flex items-center justify-center rounded-full px-0.5 leading-none"
                        >
                          {totalItems > 99 ? '99+' : totalItems}
                        </Badge>
                      )}
                    </Button>
                  </Link>

                  {/* User Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center space-x-2 text-white hover:bg-white/10 px-2 py-1.5 h-auto flex-shrink-0"
                        data-testid="button-user-menu"
                      >
                        <div className="bg-white rounded-full p-1 flex-shrink-0">
                          <User className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div className="text-left hidden lg:block min-w-0">
                          <div className="text-xs font-medium text-white truncate max-w-[120px]">{user?.fullName || user?.name || user?.email}</div>
                          <div className="text-[10px] text-white/80 truncate max-w-[120px]">{user.email}</div>
                        </div>
                        <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>
                        <div className="flex flex-col space-y-1">
                          <p className="text-sm font-medium leading-none text-foreground">{user?.fullName || user?.name || user?.email}</p>
                          <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                          <p className="text-xs leading-none text-muted-foreground capitalize">
                            {user.role === 'admin' ? 'Administrador' :
                              user.role === 'photographer' ? 'Fotógrafo' : 'Atleta'}
                          </p>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />

                      {menuItems?.map((item) => {
                        if (item.path === 'logout') return null; // Handle logout separately

                        const IconComponent = item.icon ? iconMap[item.icon] : null;
                        // Replace dynamic parts of path
                        const path = item.path.replace('${user.id}', user.id);

                        return (
                          <DropdownMenuItem key={item.id} asChild>
                            <Link to={path} className="flex items-center text-foreground hover:text-accent">
                              {IconComponent && <IconComponent className="mr-2 h-4 w-4" />}
                              <span>{item.label}</span>
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}

                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={logout} className="text-red-600">
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>Sair</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <Link to="/login">
                  <Button
                    className="bg-primary px-4 py-2 hover:bg-secondary transition-colors font-medium border-0 square-input text-primary-foreground"
                    data-testid="button-login"
                  >
                    Login
                  </Button>
                </Link>
              )}

            </nav>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-white hover:bg-white/10"
              data-testid="button-mobile-menu"
            >
              <Menu className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
